import { useState, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import api from "../services/api";

/* ═══════════════════════════════════════════════════════════════════════
 * CONFIGURAÇÃO — AJUSTE ISTO.
 * Janela em horas antes do voo em que o check-in normalmente abre.
 * Brasil doméstico ~48h. Internacional varia (24h a 72h por companhia).
 * Decide o que entra como "ABERTO" (vermelho). Valide com a operação.
 * ═══════════════════════════════════════════════════════════════════════ */
export const JANELA_CHECKIN_HORAS = 48;
export const JANELA_ATENCAO_HORAS = 96; // nível "abre em breve" (amarelo) = 4 dias

// ── Helpers FireDAC (mesmo padrão do resto do app) ───────────────────────
const extractRows = (data) => {
  if (!data) return [];
  if (data.FDBS) {
    try {
      const mgr    = data.FDBS.Manager ?? data.FDBS.manager;
      const tables = mgr?.TableList ?? mgr?.tablelist ?? [];
      if (tables.length > 0) {
        const rowList = tables[0].RowList ?? tables[0].rowlist ?? [];
        if (rowList.length > 0)
          return rowList.map((r) => r.Original ?? r.Current ?? r);
      }
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  if (Array.isArray(data)) return data;
  return [];
};

const normalize = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};

const parseData = (v) => {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};

// ── Níveis de urgência (também usados pela view) ─────────────────────────
export const NIVEIS = {
  aberto:     { rank: 0, cor: "#ff4d4f", corBg: "#fff1f0", corBorda: "#ffccc7", label: "Check-in aberto" },
  breve:      { rank: 1, cor: "#fa8c16", corBg: "#fff7e6", corBorda: "#ffe7ba", label: "Abre em breve"    },
  programado: { rank: 2, cor: "#1677ff", corBg: "#e6f4ff", corBorda: "#bae0ff", label: "Programado"       },
  passado:    { rank: 3, cor: "#8c8c8c", corBg: "#f5f5f5", corBorda: "#e8e8e8", label: "Voo já partiu"    },
};

const classificar = (dataVoo) => {
  const d = parseData(dataVoo);
  if (!d) return { nivel: "programado", horas: null, d: null };
  const horas = d.diff(dayjs(), "hour");
  if (horas < 0)                     return { nivel: "passado",    horas, d };
  if (horas <= JANELA_CHECKIN_HORAS) return { nivel: "aberto",     horas, d };
  if (horas <= JANELA_ATENCAO_HORAS) return { nivel: "breve",      horas, d };
  return { nivel: "programado", horas, d };
};

export const textoTempo = (horas) => {
  if (horas === null) return "";
  if (horas < 0)  return "voo já partiu";
  if (horas < 24) return horas <= 1 ? "menos de 1h" : `em ~${horas}h`;
  const dias = Math.floor(horas / 24);
  return dias === 1 ? "em 1 dia" : `em ${dias} dias`;
};

/* ─────────────────────────────────────────────────────────────────────────
 * useCheckinNotificacoes
 * Chame UMA vez, no nível mais alto que precisa do dado (o Dashboard).
 * Fonte única de verdade: a Sidebar usa `total` p/ a bolinha, e a tela
 * de notificações consome `ordenados/marcarCheckin`. Marcar um check-in
 * atualiza os dois ao mesmo tempo, porque é o mesmo estado.
 * ───────────────────────────────────────────────────────────────────────── */
export function useCheckinNotificacoes() {
  const [voos, setVoos]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [marcando, setMarcando] = useState({});

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await api.get("/ServerPrincipal/PesquisaNotificacoes");
      const rows = extractRows(res.data).map(normalize);
      setVoos(rows);
    } catch (e) {
      console.warn("[Notificacoes] falha ao carregar:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const ordenados = useMemo(() => {
    return [...voos]
      .map((v) => ({ ...v, _cls: classificar(v.ven_data_voo) }))
      .sort((a, b) => {
        const ra = NIVEIS[a._cls.nivel].rank;
        const rb = NIVEIS[b._cls.nivel].rank;
        if (ra !== rb) return ra - rb;
        return (a._cls.horas ?? 1e9) - (b._cls.horas ?? 1e9);
      });
  }, [voos]);

  const resumo = useMemo(() => {
    const c = { aberto: 0, breve: 0, programado: 0, passado: 0 };
    ordenados.forEach((v) => { c[v._cls.nivel]++; });
    return c;
  }, [ordenados]);

  const marcarCheckin = useCallback(async (venId) => {
    setMarcando((m) => ({ ...m, [venId]: true }));
    // Otimista: remove já da lista. Se falhar, re-busca do servidor (fonte de verdade).
    setVoos((vs) => vs.filter((v) => v.ven_id !== venId));
    try {
      await api.post("/ServerPrincipal/AtualizaNotificacoes", { Checkin: "1", Venda: venId });
    } catch (e) {
      await carregar(); // rollback: restaura o estado real do banco
      throw e;          // deixa a view exibir a mensagem de erro
    } finally {
      setMarcando((m) => { const n = { ...m }; delete n[venId]; return n; });
    }
  }, [carregar]);

  return {
    voos, ordenados, resumo, loading, marcando,
    total:        voos.length,   // ← usado na bolinha da Sidebar (todas pendentes)
    totalUrgente: resumo.aberto, // ← alternativa: só as que precisam de ação agora
    carregar, marcarCheckin,
  };
}